<template>
  <div class="container-fluid pl-0 pr-0 flex-grow-1 min-height-0 d-flex flex-column overflow-hidden">
    <div class="text-left mt-2 mb-2 flex-grow-1 min-height-0 d-flex flex-column">
      <div class="row flex-grow-1 min-height-0">
        <div class="col-6 d-flex flex-column min-height-0 h-100">
          <template v-if="uiVersion !== 'new'">
            <h6 class="flex-shrink-0">
              {{ $t("permissions.choosePermission") }}
            </h6>
            <div class="border-box flex-grow-1 min-height-0 d-flex flex-column h-100">
              <div class="flex-grow-1 min-height-0 overflow-auto">
                <div
                  v-for="el in modulesWithPermissions"
                  :key="el.name"
                >
                  <div
                    class="permission-header d-flex align-items-center"
                    @click="selectedApp === el.name ? (selectedApp = null) : (selectedApp = el.name)"
                  >
                    <img
                      class="app-logo"
                      :src="require(`@core/assets/logos/${el.name.toLowerCase()}-color-icon.png`)"
                    >
                    <div>
                      {{ el.text }}
                    </div>
                  </div>

                  <SmoothReflow>
                    <PermissionsTree
                      class="tree-lg"
                      :permissions="el.children"
                      :root="el"
                      :selected="selectedPermissionName"
                      :assigned-permissions="assignedPermissions"
                      :root-name="el.name"
                      :is-checked="false"
                      :hide-parent="true"
                      :is-expanded="selectedApp == el.name"
                      :is-clicked-from-parent="false"
                      :current-depth="0"
                      @select="selectPermission"
                    />
                  </SmoothReflow>
                </div>
              </div>

              <div
                v-if="isSysAdmin"
                class="flex-shrink-0"
                style="margin-left: 20px"
              >
                <TreeTableRowPermissions
                  class="tree-table-row-permissions"
                  :node="{ id: '1', name: $t('permissions.releaseNotesAccess') }"
                  :expanded="false"
                  :expandable="false"
                  :pending="false"
                  :padding-sum="0"
                  :selected="!!releaseNotesAccess"
                  :selectable="true"
                  @select="toggleReleaseNotesAccess"
                />
              </div>
            </div>
          </template>

          <template v-else>
            <h6 class="flex-shrink-0">
              {{ $t("permissions.choosePermission") }}
            </h6>
            <div class="border-box flex-grow-1 min-height-0 d-flex flex-column h-100">
              <PermissionsTreePanel
                class="flex-grow-1 min-height-0"
                :modules="modulesWithPermissions"
                :assigned-permissions="assignedPermissions"
                :selected="selectedPermissionName"
                @select="selectPermission"
              />

              <div
                v-if="isSysAdmin"
                class="flex-shrink-0"
                style="margin-left: 20px"
              >
                <TreeTableRowPermissions
                  class="tree-table-row-permissions"
                  :node="{ id: '1', name: $t('permissions.releaseNotesAccess') }"
                  :expanded="false"
                  :expandable="false"
                  :pending="false"
                  :padding-sum="0"
                  :selected="!!releaseNotesAccess"
                  :selectable="true"
                  @select="toggleReleaseNotesAccess"
                />
              </div>
            </div>
          </template>
        </div>

        <div
          v-if="
            selectedPermissionName === SYSTEM_ADMIN ||
              selectedPermissionName === CONFIG_RELEASE_NOTES_ACCESS
          "
        />
        <div
          v-else-if="!selectedPermissionName && uiVersion === 'new'"
          class="col-6 d-flex flex-column min-height-0 h-100"
        >
          <PermissionShowcase
            :permission="null"
            class="flex-grow-1 min-height-0"
          />
        </div>
        <div
          v-else-if="selectedPermissionName"
          class="col-6 d-flex flex-column min-height-0 h-100"
        >
          <PermissionShowcase
            v-if="uiVersion === 'new'"
            :permission="selectedPermission"
            class="mb-3 flex-shrink-0"
          />

          <div
            v-if="assignedTargets.length"
            class="panel-card assigned-targets-card flex-shrink-0"
          >
            <div class="panel-card__title">
              {{ $t('permissions.assignedTargets') }}
            </div>
            <div class="assigned-targets-card__body">
              <span
                v-for="target in assignedTargets"
                :key="target.id"
                class="assigned-target-pill"
              >
                <span
                  v-tippy
                  class="assigned-target-pill__label"
                  :content="target.displayName"
                >
                  {{ target.displayName }}
                </span>
                <i
                  v-if="!pending[target.value]"
                  v-tippy
                  class="fas fa-times assigned-target-pill__remove"
                  :content="$t('permissions.unassign')"
                  @click="togglePermission({ id: target.value }, null, null)"
                />
                <Loader
                  v-else
                  :size="10"
                  color="grey"
                />
              </span>
            </div>
          </div>

          <div class="panel-card subject-picker-card flex-grow-1 min-height-0 d-flex flex-column">
            <div class="panel-card__title">
              {{ $t('permissions.configure') }}
            </div>
            <div class="permissions-form__subject-picker flex-grow-1 min-height-0">
              <div
                v-if="!selectedPlantId && (shouldSelectElement || shouldSelectLine)"
                class="h-100 d-flex flex-column flex-grow-1 min-height-0"
              >
                <h6>{{ $t("permissions.choosePlant") }}</h6>
                <div class="border-box overflow-auto h-100 min-h-0">
                  <ListSelector
                    :list="plants"
                    @selected="selectedPlantId = $event"
                  />
                </div>
              </div>

              <div
                v-else-if="!selectedTenantId && shouldSelectGroup"
                class="h-100 d-flex flex-column flex-grow-1 min-height-0"
              >
                <h6>{{ $t("permissions.chooseTenant") }}</h6>
                <div class="border-box overflow-auto h-100 min-h-0">
                  <ListSelector
                    :list="filteredTenants"
                    @selected="selectedTenantId = $event"
                  />
                </div>
              </div>

              <div
                v-else
                class="h-100 d-flex flex-column flex-grow-1 min-height-0"
              >
                <h6>
                  <span v-if="shouldSelectElement || shouldSelectLine">
                    <span
                      v-if="plants.length > 1"
                      @click="selectedPlantId = null"
                    >
                      <i class="zoom-hoverable ion ion-ios-arrow-dropleft-circle" />
                    </span>
                    {{ $t("permissions.chooseAsset") }}
                  </span>
                  <span v-else-if="shouldSelectGroup">
                    <span
                      v-if="filteredTenants.length > 1"
                      @click="selectedTenantId = null"
                    >
                      <i class="zoom-hoverable ion ion-ios-arrow-dropleft-circle" />
                    </span>
                    {{ $t("permissions.chooseGroup") }}
                  </span>
                  <span v-else-if="shouldSelectTenant">
                    {{ $t("permissions.chooseTenant") }}
                  </span>
                  <span v-else-if="shouldSelectNone">
                    {{ $t("permissions.chooseSubPermission") }}
                  </span>
                  <span v-else>
                    {{ $t("permissions.choosePlant") }}
                  </span>
                </h6>
                <div class="border-box overflow-auto h-100 min-h-0">
                  <div>
                    <TreeTablePermissions
                      v-if="shouldSelectElement || shouldSelectLine"
                      :assigned-permissions="selectedAssignedPermissions"
                      class="tree-lg"
                      :pending="pending"
                      :list="plantElements"
                      :root="selectedPlant"
                      :selectable="true"
                      :is-expanded="false"
                      :names-of-permission-being-modified="[selectedPermissionName]"
                      @select="togglePermission($event, selectedPlant, 'PlantId')"
                    />
                    <TreeTablePermissions
                      v-else-if="shouldSelectTenant"
                      class="tree-lg"
                      :assigned-permissions="selectedAssignedPermissions"
                      :pending="pending"
                      :list="filteredTenants.map((x) => ({ ...x, parentId: '0' }))"
                      :root="{ id: '0' }"
                      :selectable="true"
                      :padding="0"
                      :hide-root="true"
                      :is-expanded="false"
                      :names-of-permission-being-modified="[selectedPermissionName]"
                      @select="togglePermission($event, $event, 'TenantId')"
                    />
                    <TreeTablePermissions
                      v-else-if="shouldSelectGroup"
                      :assigned-permissions="selectedAssignedPermissions"
                      class="tree-lg"
                      :pending="pending"
                      :list="selectedTenantRoles"
                      :root="selectedTenant"
                      :selectable="true"
                      :is-expanded="false"
                      :names-of-permission-being-modified="[selectedPermissionName]"
                      @select="togglePermission($event, selectedTenant, 'TenantId')"
                    />

                    <TreeTablePermissions
                      v-else-if="shouldSelectPlantLp"
                      class="tree-lg"
                      :assigned-permissions="selectedAssignedPermissions"
                      :pending="pending"
                      :list="plantsLpForTenant.map((x) => ({ ...x, parentId: '0' }))"
                      :root="{ id: '0' }"
                      :selectable="true"
                      :padding="0"
                      :hide-root="true"
                      :is-expanded="false"
                      :names-of-permission-being-modified="[selectedPermissionName]"
                      @select="togglePermission($event, $event, 'LpPlantId')"
                    />
                    <div v-else-if="shouldSelectNone">
                      <div class="text-center py-3 text-muted d-flex flex-column align-items-center">
                        <i class="fas fa-info-circle mb-2" />
                        <small>
                          {{ $t("permissions.chooseSubPermissionFromList") }}
                        </small>
                      </div>
                    </div>
                    <TreeTablePermissions
                      v-else
                      class="tree-lg"
                      :assigned-permissions="selectedAssignedPermissions"
                      :pending="pending"
                      :list="plants.map((x) => ({ ...x, parentId: '0' }))"
                      :root="{ id: '0' }"
                      :selectable="true"
                      :padding="0"
                      :hide-root="true"
                      :is-expanded="false"
                      :names-of-permission-being-modified="[selectedPermissionName]"
                      @select="togglePermission($event, $event, 'PlantId')"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="text-center mt-3" />
    </div>
  </div>
</template>

<script>
import PermissionsTree from '@/components/permissions/PermissionsTree';
import PermissionsTreePanel from '@/components/permissions/PermissionsTreePanel';
import PermissionShowcase from '@/components/permissions/PermissionShowcase';
import TreeTablePermissions from '@/components/tables/TreeTablePermissions';
import TreeTableRowPermissions from '@/components/tables/TreeTableRowPermissions';
import ListSelector from '@/components/utils/ListSelector';
import { elementType } from '@/utils/dictionary';
import { handleApiError } from '@/utils/handleApiError';
import { CONFIG_RELEASE_NOTES_ACCESS, SYSTEM_ADMIN } from '@core/dict/permissions';
import { mapActions, mapGetters, mapState } from 'vuex';

export default {
  props: {
    user: Object,
    roleId: String,
    uiVersion: {
      type: String,
      default: 'classic',
    },
  },
  data: () => ({
    SYSTEM_ADMIN,
    CONFIG_RELEASE_NOTES_ACCESS,
    selectedPlantId: null,
    selectedTenantId: null,
    selectedPermissionName: '',
    selectedApp: '',
    pending: {},
    rolesPending: false,
    roles: [],
    error: null,
  }),
  components: {
    TreeTablePermissions,
    TreeTableRowPermissions,
    PermissionsTree,
    PermissionsTreePanel,
    PermissionShowcase,
    ListSelector,
  },
  computed: {
    ...mapState({
      plants: state => state.structure.plants || [],
      plantsLp: state => state.plantsLp.plantsLp || [],
      tenantRoles: state => state.role.roles || [],
      allRoles: state => state.role.allRoles || [],
      tenants: state => state.tenant.tenants || [],
      elements: state => state.structure.elements || [],
    }),
    ...mapGetters('core', ['isSysAdmin', 'tenantModules', 'can']),
    ...mapGetters(['permissionTypes', 'permissionTypeList', 'tenantId']),
    filteredTenants() {
      return this.tenants.filter(
        x =>
          this.can(this.$perm.CONFIG_TENANT_ADMIN, x.id)
          || this.can(this.$perm.CONFIG_USER_MANAGEMENT, x.id),
      );
    },
    plantsLpForTenant() {
      return this.plantsLp.filter(p => p.tenantId === this.tenantId);
    },
    modulesWithPermissions() {
      return this.permissionTypes.filter(
        x =>
          x.name.toLowerCase() === 'hub'
          || this.tenantModules.some(m => m.module.toLowerCase() === x.name.toLowerCase()),
      );
    },
    selectedAssignedPermissions() {
      return this.assignedPermissions.filter(x => x.name === this.selectedPermissionName);
    },
    assignedTargets() {
      const targets = this.selectedAssignedPermissions
        .map(x => {
          const resolved = this.resolveTarget(x.value);
          return resolved && { id: x.id, value: x.value, ...resolved };
        })
        .filter(x => x);

      const hasMultiplePlants = new Set(targets.map(x => x.plantName).filter(x => x)).size > 1;

      return targets
        .map(x => ({
          ...x,
          displayName: hasMultiplePlants && x.plantName ? `${x.plantName} / ${x.name}` : x.name,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { numeric: true, sensitivity: 'base' }));
    },
    assignedPermissions() {
      return this.user?.permissions || [];
    },
    role() {
      if (!this.roleId) return null;
      return this.tenantRoles.find(x => x.id === this.roleId);
    },
    selectedPlant() {
      return this.plants.find(x => x.id === this.selectedPlantId);
    },
    selectedTenant() {
      return this.filteredTenants.find(x => x.id === this.selectedTenantId);
    },
    childrenPermissionsOfSelectedPermission() {
      if (!this.shouldSelectPlantLp) return [];

      const findPermission = (permissions, name) =>
        permissions.reduce((found, permission) => {
          if (found) return found;
          if (permission.name === name) {
            return permission;
          }
          if (permission.children) {
            return findPermission(permission?.children || [], name);
          }
          return null;
        }, null);
      return findPermission(this.permissionTypes, this.selectedPermissionName)?.children || [];
    },
    plantElements() {
      const plantElements = this.elements.filter(x => x.plantId === this.selectedPlantId);

      if (this.shouldSelectLine) {
        const lines = plantElements.filter(
          x => x.type.toLowerCase() === elementType.line.toLowerCase(),
        );

        const getParents = (allElements, children) => {
          if (children.length === 0) return [];

          const parentDict = children
            .map(c => allElements.find(x => x.id === c.parentId))
            .filter(x => x)
            .reduce((acc, curr) => {
              acc[curr.id] = curr;
              return acc;
            }, {});

          const parents = Object.values(parentDict);

          return parents.concat(getParents(allElements, parents));
        };

        return lines.concat(getParents(plantElements, lines));
      }

      return plantElements;
    },
    selectedPermission() {
      return this.permissionTypeList.find(x => x.name === this.selectedPermissionName);
    },
    shouldSelectPlantLp() {
      return this.selectedPermission?.arg.toLowerCase() === 'lpplantid';
    },
    shouldSelectElement() {
      return this.selectedPermission?.arg.toLowerCase() === 'elementid';
    },
    shouldSelectGroup() {
      return this.selectedPermission?.arg.toLowerCase() === 'groupid';
    },
    shouldSelectTenant() {
      return this.selectedPermission?.arg.toLowerCase() === 'tenantid';
    },
    shouldSelectLine() {
      return this.selectedPermission?.arg.toLowerCase() === 'lineid';
    },
    shouldSelectNone() {
      return this.selectedPermission?.arg.toLowerCase() === 'none';
    },
    sysAdmin() {
      return this.assignedPermissions.find(
        x => x.name.toLowerCase() === SYSTEM_ADMIN.toLowerCase(),
      );
    },
    releaseNotesAccess() {
      return this.assignedPermissions.find(x => x.name === CONFIG_RELEASE_NOTES_ACCESS);
    },
    selectedTenantRoles() {
      return this.allRoles
        .map(x => ({
          ...x,
          parentId: x.tenantId,
        }))
        .filter(x => x.tenantId === this.selectedTenantId);
    },
  },
  methods: {
    ...mapActions([
      'setUserPermission',
      'setRolePermission',
      'resetUserPermission',
      'resetRolePermission',
    ]),
    resolveTarget(value) {
      if (this.shouldSelectElement || this.shouldSelectLine) {
        const element = this.elements.find(x => x.id === value);
        if (!element) return null;
        return { name: element.name, plantName: this.plants.find(p => p.id === element.plantId)?.name };
      }
      if (this.shouldSelectTenant) {
        const tenant = this.tenants.find(x => x.id === value);
        return tenant && { name: tenant.name };
      }
      if (this.shouldSelectGroup) {
        const role = this.allRoles.find(x => x.id === value);
        return role && { name: role.name };
      }
      if (this.shouldSelectPlantLp) {
        const plantLp = this.plantsLp.find(x => x.id === value);
        return plantLp && { name: plantLp.name };
      }
      const plant = this.plants.find(x => x.id === value);
      return plant && { name: plant.name };
    },
    toggleReleaseNotesAccess() {
      this.selectedPermissionName = CONFIG_RELEASE_NOTES_ACCESS;
      this.togglePermission({ id: '1' }, null, null);
    },
    selectPermission(permission) {
      if (permission === this.selectedPermissionName) {
        this.selectedPermissionName = null;
      } else {
        this.selectedPermissionName = permission;

        if (this.plants.length === 1) {
          this.selectedPlantId = this.plants[0].id;
        }
      }
    },
    togglePermission(arg, rootArg, rootType) {
      if (!arg) return;

      const rootId = rootArg !== null ? rootArg.id : null;
      const ev = arg.id;

      const perm = this.assignedPermissions.find(
        x => x.name === this.selectedPermissionName && x.value.toLowerCase() === ev.toLowerCase(),
      );

      this.$set(this.pending, ev, true);
      this.error = null;

      if (!perm) {
        this.addPermission(ev, rootId, rootType)
          .catch(({ response }) => {
            this.error = handleApiError(response);
          })
          .finally(() => {
            this.$set(this.pending, ev, false);
            this.$root.$bvToast.toast(this.$t('permissions.updateSuccessfully'), {
              title: this.$t('general.requestReceived'),
              variant: 'success',
              appendToast: true,
            });
          });
      } else {
        this.removePermission(ev)
          .catch(({ response }) => {
            this.error = handleApiError(response);
          })
          .finally(() => {
            this.$set(this.pending, ev, false);

            this.$root.$bvToast.toast(this.$t('permissions.updateSuccessfully'), {
              title: this.$t('general.requestReceived'),
              variant: 'success',
              appendToast: true,
            });
          });
      }
    },
    addPermission(val, rootId, rootType) {
      const childrenPermissions = this.childrenPermissionsOfSelectedPermission.filter(
        c =>
          !this.assignedPermissions.find(
            p => p.name === c.name && p.value.toLowerCase() === val.toLowerCase(),
          ),
      );
      if (childrenPermissions.length) {
        return Promise.all([
          ...childrenPermissions.map(child =>
            this.addUserPermission(val, rootId, rootType, child.name)),
          this.addUserPermission(val, rootId, rootType),
        ]);
      }

      return this.addUserPermission(val, rootId, rootType);
    },
    addUserPermission(val, rootId, rootType, permissionName = undefined) {
      return this.setUserPermission({
        params: {
          id: this.user.id,
        },
        data: {
          name: permissionName || this.selectedPermissionName,
          value: val,
          rootId,
          rootType,
        },
      });
    },
    removePermission(val) {
      const childrenPermissions = [
        ...this.childrenPermissionsOfSelectedPermission.filter(p =>
          this.assignedPermissions.find(
            x => x.name === p.name && x.value.toLowerCase() === val.toLowerCase(),
          )),
      ];
      if (childrenPermissions.length) {
        return Promise.all([
          ...childrenPermissions.map(child => this.removeUserPermission(val, child.name)),
          this.removeUserPermission(val),
        ]);
      }

      return this.removeUserPermission(val);
    },
    removeUserPermission(val, permissionName = undefined) {
      const permission = this.user.permissions.find(
        x =>
          x.name === (permissionName || this.selectedPermissionName)
          && x.value.toLowerCase() === val.toLowerCase(),
      );

      if (!permission) return Promise.reject();

      return this.resetUserPermission({
        params: {
          id: this.user.id,
          permissionId: permission.id,
        },
      });
    },
    removeRolePermission(val) {
      if (!this.selectedPermissionName) return Promise.reject();

      const permission = this.role.permissions.find(
        x => x.name === this.selectedPermissionName && x.value.toLowerCase() === val.toLowerCase(),
      );

      if (!permission) return Promise.reject();

      return this.resetRolePermission({
        params: {
          tenantId: this.tenantId,
          id: this.role.id,
          permissionId: permission.id,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.tree-table-row-permissions {
  margin-top: 5px !important;
  margin-left: -40px !important;
}

.border-box {
  background-color: rgb(245, 245, 245);
  padding: 10px;
  border-radius: 10px;
}

.panel-card {
  background-color: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 10px;
  overflow: hidden;
}

.panel-card__title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #868e96;
  margin-bottom: 6px;
  flex-shrink: 0;
}

.assigned-targets-card {
  margin-bottom: 10px;
}

.assigned-targets-card__body {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.subject-picker-card {
  margin-bottom: 0;
}

.assigned-target-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 20px;
  background-color: #646668;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
}

.assigned-target-pill__label {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: top;
}

.assigned-target-pill__remove {
  cursor: pointer;
  font-size: 10px;
  color: #fff;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    color: #ff8a80;
  }
}

.zoom-hoverable {
  cursor: pointer;

  &:hover {
    transform: scale(1.5);
  }
}

.permission-header {
  padding: 2px 5px;
  background-color: #fff;
  border-radius: 5px;
  margin-top: 5px;
  cursor: pointer;
  border: 1px solid transparent;

  &:hover {
    border-color: #333;
  }

  .app-logo {
    max-height: 25px;
    padding-right: 5px;
  }
}
</style>
